'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';

import useUser, { User } from '@/state/user';
import useTenant from '@/state/tenant';
import useAppearanceSettings from '@/state/appearance-settings';
import {
  posthog,
  isPostHogEnabled,
  registerSuperProperties,
} from '@/lib/posthog';
import { setMetaAdvancedMatching } from '@/lib/analytics';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { resolveRedirectPath } from '@/lib/utils';
import { getBookingPageStatus, type SiteStatus } from '@/helpers/tenant';
import { useSiteFailure } from '@/hooks/use-site-failure';
import type { SiteFailure } from '@/lib/site-failure-store';

interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User | undefined;
  bookingPageStatus: SiteStatus;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// The tenant query is seeded from the server and refetches only on a refocused
// tab, and a refetch that fails over data on screen never enters the store, so
// its error only ever describes a load with nothing to show. The store carries
// what every other query has seen since, which is why it is read first.
// Reading it can only turn an open page dark, never a dark page open: when the
// store is empty or holds unreachable, the tenant error keeps the full power
// it has today.
const resolveBookingPageStatus = (
  siteFailure: SiteFailure | null,
  tenantError: unknown
): SiteStatus => {
  if (siteFailure?.kind === 'dark') return siteFailure.reason;
  // Every surface reads this one answer, so the outage has to be expressible
  // here. Left out, an embedded widget blames the product for a backend that
  // is simply down, on the studio's own website.
  if (siteFailure?.kind === 'unreachable') return 'unreachable';
  return getBookingPageStatus(tenantError);
};

// An embed runs in a cross-site frame where our session cookie is third-party,
// so /public/users/me can only ever 401 — and that 401 drives the refresh path
// into clearing the frame's storage. Declare the frame anonymous instead of
// paying for a login that cannot succeed. The tenant call still runs: a dark
// studio must read as dark in its own embedded widget, not as a missing class.
export function AnonymousAuthProvider({ children }: { children: React.ReactNode }) {
  const {
    data: tenant,
    isLoading: isTenantLoading,
    error: tenantError,
  } = useTenant();
  const siteFailure = useSiteFailure();

  // The embed never mounts AuthProvider, so without this every event fired from
  // a widget lands in the shared project with no tenant to file it under.
  useEffect(() => {
    if (!tenant?.slug || !isPostHogEnabled()) return;
    registerSuperProperties(tenant.slug);
  }, [tenant?.slug]);

  return (
    <AuthContext.Provider
      value={{
        isLoading: isTenantLoading,
        isAuthenticated: false,
        user: undefined,
        bookingPageStatus: resolveBookingPageStatus(siteFailure, tenantError),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// The public home page is for signed-out visitors; a signed-in one is sent on
// to the dashboard, or to the page a login link asked for.
export const redirectsSignedInVisitor = (pathname: string | null): boolean =>
  pathname === '/';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { data: user, isLoading: isUserLoading } = useUser();
  const {
    data: tenant,
    isLoading: isTenantLoading,
    error: tenantError,
  } = useTenant();
  const { isLoading: isAppearanceSettingsLoading } = useAppearanceSettings();
  const siteFailure = useSiteFailure();

  const isLoading =
    isUserLoading || isTenantLoading || isAppearanceSettingsLoading;
  const isAuthenticated = !!user;
  const bookingPageStatus = resolveBookingPageStatus(siteFailure, tenantError);

  const prevAuthRef = useRef<boolean | null>(null);

  // Latch once auth first resolves, so a background refetch flipping isLoading
  // back to true doesn't send consumers back to their loading state.
  const [hasResolved, setHasResolved] = useState(false);
  if (!isLoading && !hasResolved) setHasResolved(true);

  useEffect(() => {
    if (isLoading || !isPostHogEnabled()) return;

    if (isAuthenticated && user) {
      posthog.identify(user.user_id, {
        email: user.email,
        name: user.first_name,
      });
      void setMetaAdvancedMatching({
        email: user.email,
        phone: user.phone,
        first_name: user.first_name,
        last_name: user.last_name,
        country_code: user.country_code,
        user_id: user.user_id,
      });
    } else {
      if (prevAuthRef.current === true) {
        track({ event: AnalyticsEvent.LOGOUT });
      }
      // A re-run with no change in auth must keep the anonymous identity.
      if (prevAuthRef.current !== false) posthog.reset();
    }

    // After identify and reset alike: reset wipes every super property, and the
    // tenant record is not resolved yet when posthog.init() first registers
    // them, so this is the only point where both are known.
    registerSuperProperties(tenant?.slug);

    prevAuthRef.current = isAuthenticated;
  }, [
    isLoading,
    isAuthenticated,
    user?.user_id,
    user?.email,
    user?.first_name,
    tenant?.slug,
  ]);

  useEffect(() => {
    if (isLoading) return;
    // A dark studio refuses the session check with the studio's own code, not
    // the visitor's, so no user here says nothing about the session. Bouncing
    // to sign-in would ask a member to log in to a page that is switched off;
    // the gate renders the dark state on the URL they asked for instead.
    if (bookingPageStatus !== 'available') return;

    if (!isAuthenticated && pathname?.startsWith('/dashboard')) {
      const search = searchParams.toString();
      const target = `${pathname}${search ? `?${search}` : ''}`;
      router.replace(`/?action=login&redirect=${encodeURIComponent(target)}`);
    } else if (isAuthenticated && redirectsSignedInVisitor(pathname)) {
      router.replace(resolveRedirectPath(searchParams.get('redirect')));
    }
  }, [
    isLoading,
    bookingPageStatus,
    isAuthenticated,
    pathname,
    searchParams,
    router,
  ]);

  return (
    <AuthContext.Provider
      value={{
        isLoading: !hasResolved,
        isAuthenticated,
        user,
        bookingPageStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
